import { TextInput } from 'lego-on-react';
import React, { useCallback, useMemo } from 'react';

import { lengthConfig } from '@paskills/validation';
import i18n from '@yandex-int/i18n';
import * as languagesKeyset from 'i18n/Language';
import * as settingsKeyset from 'i18n/SettingsLabels';
import * as smartHomeFormKeyset from 'i18n/SmartHomeForm';
import block from 'propmods';

import { ErrorForLanguageForm, LanguageCode, SupportedLanguageFormData } from '../../../types';
import { Field } from '../../Form/Form';
import TextArea from '../../Form/TextArea';
import { Select } from '../../Select/Select';
import AddLanguage from '../AddLanguage';

import { DEFAULT_EXTERNAL_DESCRIPTION } from './content';
import { LanguageDeleteButton } from './LanguageDeleteButton';

import './LanguageEditComponent.scss';

const tSettings = i18n(settingsKeyset);
const tSmartHome = i18n(smartHomeFormKeyset);
const tLanguage = i18n(languagesKeyset);

const b = block('LanguageEditComponent');

interface Props {
    isDraft: boolean;
    isTrustedSmartHomeSkill: boolean;
    languages: SupportedLanguageFormData[];
    // ErrorForLanguageForm object in a serialized form for each LanguageSetup component
    serializedErrorsForLanguage: string[] | undefined;
    setSupportedLanguages: (value: SupportedLanguageFormData[]) => void;
    disabled: boolean;
}

interface LanguageSetupProps {
    data: SupportedLanguageFormData;
    isDraft: boolean;
    isTrustedSmartHomeSkill: boolean;
    languageOptions: { value: string; text: string }[];
    onFieldChange: (updatedValues: SupportedLanguageFormData) => void;
    errors: ErrorForLanguageForm;
    disabled: boolean;
}

export function isAvailableLanguage(lang: string): lang is LanguageCode {
    return lang in languagesKeyset.en;
}

export const getRtlByLanguageCode = (data: SupportedLanguageFormData) => data?.languageCode === 'ar';

const LanguageSetup: React.FC<LanguageSetupProps> = ({
    data,
    isDraft,
    isTrustedSmartHomeSkill,
    languageOptions,
    onFieldChange,
    errors,
    disabled,
}) => {
    const externalSettingsDescription = data.externalSettingsDescription || '';
    const handleLangChange = (selectedLanguage: string) => {
        if (!(selectedLanguage && isAvailableLanguage(selectedLanguage))) {
            return;
        }
        const updatedValues: SupportedLanguageFormData = { languageCode: selectedLanguage };
        const defaultExternalDescription = selectedLanguage && DEFAULT_EXTERNAL_DESCRIPTION?.[selectedLanguage];
        const isCurrentExternalDescriptionOneOfDefaults =
            Object.values(DEFAULT_EXTERNAL_DESCRIPTION).includes(externalSettingsDescription);
        if ((!externalSettingsDescription || isCurrentExternalDescriptionOneOfDefaults) && defaultExternalDescription) {
            updatedValues['externalSettingsDescription'] = defaultExternalDescription;
        }
        onFieldChange(updatedValues);
    };
    // TODO: remove fallback to .description after all multilang users updated
    const alreadyPublishedDescription = !isDraft && !externalSettingsDescription && data.description;

    return (
        <div>
            <Field
                required
                label={tSettings('language')}
                caption={tSettings('language-description')}
                error={errors.languageDisplayName}
            >
                <Select
                    label={tSettings('language')}
                    placeholder={tSettings('select-language')}
                    type="radio"
                    value={data.languageCode}
                    onChange={(selectedLang) => handleLangChange(selectedLang)}
                    options={languageOptions}
                    disabled={disabled}
                />
            </Field>
            <Field
                required
                label={tSettings('just-name')}
                error={errors.appName}
                caption={tSmartHome('skill-name')}
                rtl={getRtlByLanguageCode(data)}
            >
                <TextInput
                    theme="normal"
                    size="s"
                    text={data.appName}
                    onChange={(text) => onFieldChange({ appName: text })}
                    autocomplete={false}
                    disabled={disabled}
                />
            </Field>
            <Field label={tSettings('subtitle')} error={errors.subtitle} rtl={getRtlByLanguageCode(data)}>
                <TextArea
                    rows={1}
                    limit={lengthConfig.secondaryTitle}
                    text={data.subtitle || ''}
                    onChange={(text) => onFieldChange({ subtitle: text })}
                    disabled={disabled}
                    rtl={getRtlByLanguageCode(data)}
                />
            </Field>
            {alreadyPublishedDescription ? (
                <Field
                    required
                    label={tSettings('description')}
                    error={errors.description}
                    rtl={getRtlByLanguageCode(data)}
                >
                    <TextArea
                        rows={4}
                        limit={lengthConfig.description}
                        text={alreadyPublishedDescription || ''}
                        onChange={() => {}}
                        disabled
                        rtl={getRtlByLanguageCode(data)}
                    />
                </Field>
            ) : (
                <>
                    <Field
                        required
                        label={tSettings('description-devices-connection')}
                        error={errors.externalSettingsDescription}
                        rtl={getRtlByLanguageCode(data)}
                    >
                        <TextArea
                            rows={4}
                            limit={lengthConfig.description}
                            text={externalSettingsDescription}
                            onChange={(text) => onFieldChange({ externalSettingsDescription: text })}
                            disabled={disabled}
                            rtl={getRtlByLanguageCode(data)}
                        />
                    </Field>
                    <Field
                        required
                        label={tSettings('description-list-of-supported-devices')}
                        error={errors.supportedUnitsDescription}
                        rtl={getRtlByLanguageCode(data)}
                    >
                        <TextArea
                            rows={4}
                            limit={lengthConfig.description}
                            text={data.supportedUnitsDescription || ''}
                            onChange={(text) => onFieldChange({ supportedUnitsDescription: text })}
                            disabled={disabled}
                            rtl={getRtlByLanguageCode(data)}
                        />
                    </Field>
                    <Field
                        required={isTrustedSmartHomeSkill}
                        label={tSettings('description-support-url')}
                        error={errors.supportServiceUrl}
                    >
                        <TextInput
                            theme="normal"
                            size="s"
                            text={data.supportServiceUrl}
                            type="url"
                            onChange={(text) => onFieldChange({ supportServiceUrl: text })}
                            disabled={disabled}
                        />
                    </Field>
                </>
            )}
        </div>
    );
};

export const LanguagesEditComponent: React.FC<Props> = ({
    isDraft,
    isTrustedSmartHomeSkill,
    languages,
    serializedErrorsForLanguage,
    setSupportedLanguages,
    disabled,
}) => {
    const onChangeTemplate =
        (updatedLangIndex: number) =>
        (updatedValues: SupportedLanguageFormData = {}) => {
            const result: SupportedLanguageFormData[] = languages.map((langSettingData, langSettingIndex) => {
                const newEntries = updatedLangIndex === langSettingIndex ? updatedValues : {};
                return { ...langSettingData, supportServiceUrlIsRequired: isTrustedSmartHomeSkill, ...newEntries };
            });
            setSupportedLanguages(result);
        };

    const onRemoveLanguage = useCallback(
        (idx: number) => () => {
            const result: SupportedLanguageFormData[] = languages.map((lang) => Object.assign({}, lang));
            result.splice(idx, 1);
            setSupportedLanguages(result);
        },
        [setSupportedLanguages, languages],
    );

    const currentErrors = useMemo<ErrorForLanguageForm[]>(() => {
        if (serializedErrorsForLanguage === undefined || serializedErrorsForLanguage.length !== languages.length) {
            // if number of errors are not equal to number of languages skip showing errors
            // (until lengths will be consistent)
            return languages.map(() => ({}));
        }

        return serializedErrorsForLanguage.map((value) => JSON.parse(value) as ErrorForLanguageForm);
    }, [serializedErrorsForLanguage, languages]);

    const languageOptions = useMemo(() => {
        return Object.keys(languagesKeyset.en).map((value) => ({ value, text: tLanguage(value as LanguageCode) }));
    }, []);

    const handleAddLanguage = useCallback(() => {
        const languagesCopy: SupportedLanguageFormData[] = languages.map((lang) => Object.assign({}, lang));
        languagesCopy.push({});
        setSupportedLanguages(languagesCopy);
    }, [languages, setSupportedLanguages]);

    const languageLimitIsReached = languages.length === languageOptions.length;

    return (
        <div {...b()}>
            {languages.map((lang, idx) => {
                return (
                    <div key={idx}>
                        <LanguageSetup
                            isDraft={isDraft}
                            isTrustedSmartHomeSkill={isTrustedSmartHomeSkill}
                            onFieldChange={onChangeTemplate(idx)}
                            data={lang}
                            languageOptions={languageOptions}
                            errors={currentErrors[idx]}
                            disabled={disabled}
                        />
                        {idx !== languages.length - 1 && (
                            <header {...b('header')}>
                                <h4>{tSmartHome('additional-language')}</h4>
                                <LanguageDeleteButton disabled={disabled} onClick={onRemoveLanguage(idx + 1)} />
                            </header>
                        )}
                    </div>
                );
            })}
            <AddLanguage
                text={tSmartHome('add-language')}
                inProgress={false}
                disabled={disabled || languageLimitIsReached}
                onClick={handleAddLanguage}
            />
        </div>
    );
};
